import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormLabel,
    LinearProgress,
    NativeSelect,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MASS_OPERATION_TYPE, REFUND_MODE } from './bookingsMassActionHelpers';
import { formatCentsToUsd } from '../../../../helpers/formatCentsToUsd';

const PREVIEW_GRID_COLUMNS = '44px 100px minmax(0,1fr) minmax(120px,160px) minmax(120px,160px)';

function BookingsMassActionDialog({
    open,
    selectedCount,
    massOperationType,
    setMassOperationType,
    refundMode,
    setRefundMode,
    refundValue,
    setRefundValue,
    isExecutingMassAction,
    hasCompletedExecution,
    massResults,
    massExecutionProgress,
    executionPercent,
    isInvalidCancelRefundAmount,
    maxRefundableSummary,
    massRefundPreview,
    setBookingIncludedInMassAction,
    onClose,
    onExecute,
}) {
    const { t } = useTranslation();
    const operationHintKey =
        massOperationType === MASS_OPERATION_TYPE.CANCEL_AND_REFUND
            ? 'bookings.massAction.operationHintCancelAndRefund'
            : massOperationType === MASS_OPERATION_TYPE.CANCEL_WITHOUT_REFUND
              ? 'bookings.massAction.operationHintCancelWithoutRefund'
              : 'bookings.massAction.operationHintRefundOnly';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    color: 'common.black',
                    fontWeight: 700,
                    pt: 3,
                }}
            >
                {t('bookings.massAction.modalTitle', { count: selectedCount })}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Alert
                        severity="warning"
                        sx={{ '& .MuiAlert-message': { width: '100%', textAlign: 'left' } }}
                    >
                        {t('bookings.massAction.warning')}
                    </Alert>

                    {!hasCompletedExecution && (
                        <>
                            <FormControl fullWidth disabled={isExecutingMassAction}>
                                <FormLabel>{t('bookings.massAction.operationType')}</FormLabel>
                                <NativeSelect
                                    value={massOperationType}
                                    onChange={(event) => setMassOperationType(event.target.value)}
                                    sx={{ mt: 0.5 }}
                                >
                                    <option value={MASS_OPERATION_TYPE.CANCEL_AND_REFUND}>
                                        {t('bookings.massAction.cancelAndRefund')}
                                    </option>
                                    <option value={MASS_OPERATION_TYPE.CANCEL_WITHOUT_REFUND}>
                                        {t('bookings.massAction.cancelWithoutRefund')}
                                    </option>
                                    <option value={MASS_OPERATION_TYPE.REFUND_ONLY}>
                                        {t('bookings.massAction.refundOnly')}
                                    </option>
                                </NativeSelect>
                            </FormControl>
                            <Alert
                                severity="info"
                                sx={{ '& .MuiAlert-message': { width: '100%', textAlign: 'left' } }}
                            >
                                {t(operationHintKey)}
                            </Alert>

                            {massOperationType !== MASS_OPERATION_TYPE.CANCEL_WITHOUT_REFUND && (
                                <FormControl fullWidth disabled={isExecutingMassAction}>
                                    <FormLabel>
                                        {t('bookings.massAction.refundCalculation')}
                                    </FormLabel>
                                    <NativeSelect
                                        value={refundMode}
                                        onChange={(event) => {
                                            const nextMode = event.target.value;
                                            setRefundMode(nextMode);
                                            if (nextMode === REFUND_MODE.FULL) {
                                                setRefundValue('100');
                                            }
                                            if (nextMode === REFUND_MODE.FIXED) {
                                                setRefundValue('0.01');
                                            }
                                        }}
                                        sx={{ mt: 0.5 }}
                                    >
                                        <option value={REFUND_MODE.FULL}>
                                            {t('bookings.massAction.refundFull')}
                                        </option>
                                        <option value={REFUND_MODE.PERCENTAGE}>
                                            {t('bookings.massAction.refundPercentage')}
                                        </option>
                                        <option value={REFUND_MODE.FIXED}>
                                            {t('bookings.massAction.refundFixed')}
                                        </option>
                                    </NativeSelect>
                                </FormControl>
                            )}

                            {massOperationType !== MASS_OPERATION_TYPE.CANCEL_WITHOUT_REFUND &&
                                refundMode !== REFUND_MODE.FULL && (
                                    <TextField
                                        type="number"
                                        label={
                                            refundMode === REFUND_MODE.PERCENTAGE
                                                ? t('bookings.massAction.percentageInputLabel')
                                                : t('bookings.massAction.fixedInputLabel')
                                        }
                                        value={refundValue}
                                        onChange={(event) => setRefundValue(event.target.value)}
                                        inputProps={{
                                            min:
                                                massOperationType ===
                                                    MASS_OPERATION_TYPE.CANCEL_AND_REFUND &&
                                                refundMode !== REFUND_MODE.FULL
                                                    ? refundMode === REFUND_MODE.PERCENTAGE
                                                        ? 1
                                                        : 0.01
                                                    : 0,
                                            max:
                                                refundMode === REFUND_MODE.PERCENTAGE
                                                    ? 100
                                                    : undefined,
                                            step: refundMode === REFUND_MODE.PERCENTAGE ? 1 : 0.01,
                                        }}
                                        disabled={isExecutingMassAction}
                                        fullWidth
                                    />
                                )}

                            {massOperationType !== MASS_OPERATION_TYPE.CANCEL_WITHOUT_REFUND && (
                                <Box
                                    sx={{
                                        width: '100%',
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: 'grey.50',
                                        textAlign: 'left',
                                    }}
                                >
                                    {maxRefundableSummary.loading ? (
                                        <Typography variant="body2">
                                            {t('common.loading')}
                                        </Typography>
                                    ) : maxRefundableSummary.error ? (
                                        <Typography variant="body2" color="error.main">
                                            {maxRefundableSummary.error}
                                        </Typography>
                                    ) : (
                                        <>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {t('bookings.massAction.maxRefundableTotal')}{' '}
                                                {formatCentsToUsd(maxRefundableSummary.totalAmount)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {t('bookings.massAction.plannedRefundTotal')}{' '}
                                                {formatCentsToUsd(massRefundPreview.totalAmount)}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    mt: 1,
                                                    width: '100%',
                                                    maxHeight: 260,
                                                    overflowY: 'auto',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    bgcolor: 'background.paper',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: PREVIEW_GRID_COLUMNS,
                                                        px: 1.5,
                                                        py: 0.75,
                                                        borderBottom: '1px solid',
                                                        borderColor: 'divider',
                                                        position: 'sticky',
                                                        top: 0,
                                                        bgcolor: 'background.paper',
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontWeight: 700,
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        &nbsp;
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ fontWeight: 700 }}
                                                    >
                                                        {t('bookings.massAction.gridHeaderId')}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ fontWeight: 700 }}
                                                    >
                                                        {t('bookings.massAction.gridHeaderBooker')}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ textAlign: 'right', fontWeight: 700 }}
                                                    >
                                                        {t('bookings.massAction.gridHeaderPlanned')}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ textAlign: 'right', fontWeight: 700 }}
                                                    >
                                                        {t('bookings.massAction.gridHeaderMax')}
                                                    </Typography>
                                                </Box>
                                                {massRefundPreview.bookings.map(
                                                    (booking, index) => (
                                                        <Box
                                                            key={booking.id}
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns:
                                                                    PREVIEW_GRID_COLUMNS,
                                                                px: 1.5,
                                                                py: 0.75,
                                                                borderBottom:
                                                                    index ===
                                                                    massRefundPreview.bookings
                                                                        .length -
                                                                        1
                                                                        ? 'none'
                                                                        : '1px solid',
                                                                borderColor: 'divider',
                                                                opacity: booking.included ? 1 : 0.6,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <Checkbox
                                                                    size="small"
                                                                    sx={{ p: 0 }}
                                                                    checked={booking.included}
                                                                    onChange={(event) =>
                                                                        setBookingIncludedInMassAction(
                                                                            booking.id,
                                                                            event.target.checked,
                                                                        )
                                                                    }
                                                                    disabled={isExecutingMassAction}
                                                                />
                                                            </Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ fontWeight: 700 }}
                                                            >
                                                                #{booking.id}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    pr: 1,
                                                                }}
                                                            >
                                                                {booking.bookingName}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    textAlign: 'right',
                                                                    fontFamily: 'monospace',
                                                                }}
                                                            >
                                                                {booking.included
                                                                    ? formatCentsToUsd(
                                                                          booking.plannedAmount,
                                                                      )
                                                                    : '-'}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    textAlign: 'right',
                                                                    fontFamily: 'monospace',
                                                                }}
                                                            >
                                                                {formatCentsToUsd(
                                                                    booking.maxAmount,
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    ),
                                                )}
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            )}
                        </>
                    )}

                    {(isExecutingMassAction || massResults.length > 0) && (
                        <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {isExecutingMassAction
                                    ? t('bookings.massAction.runningProgress')
                                    : t('bookings.massAction.resultTitle')}
                            </Typography>
                            <LinearProgress variant="determinate" value={executionPercent} />
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {t('bookings.massAction.progressLabel', {
                                    completed: massExecutionProgress.completed,
                                    total: massExecutionProgress.total,
                                    percent: executionPercent,
                                })}
                            </Typography>
                        </Box>
                    )}

                    {massResults.length > 0 && (
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                maxHeight: 280,
                                overflowY: 'auto',
                                textAlign: 'left',
                            }}
                        >
                            {massResults.map((result) => (
                                <Box
                                    key={result.id}
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography variant="body2" fontWeight="bold">
                                        #{result.id} - {result.bookingName}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color={
                                            result.status === 'success'
                                                ? 'success.main'
                                                : 'error.main'
                                        }
                                    >
                                        {result.status === 'success'
                                            ? t('bookings.massAction.statusSuccess')
                                            : t('bookings.massAction.statusFailed')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {result.message}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    justifyContent: 'flex-end',
                    px: 3,
                    pb: 3,
                    gap: 1,
                }}
            >
                <Button onClick={onClose} disabled={isExecutingMassAction}>
                    {t('common.close')}
                </Button>
                {!hasCompletedExecution && (
                    <Button
                        variant="contained"
                        onClick={onExecute}
                        disabled={
                            isExecutingMassAction ||
                            selectedCount === 0 ||
                            isInvalidCancelRefundAmount
                        }
                    >
                        {t('bookings.massAction.executeButton')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default BookingsMassActionDialog;
